import type { KeyedMutator } from 'swr';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Copy, Check, Palette, Info } from 'lucide-react';
import { HslColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import axiosInstance from '@/lib/axios';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { NavigationSettings } from './NavigationSettings';
import type { Community, NavigationConfig } from '@/types';
import { DEFAULT_PRIMARY } from '@/lib/brand-theme';
import {
  getContrastVsWhite,
  hexToHsl,
  hslStringToObj,
  hslToHex,
  objToHslString,
} from '@/lib/hsl-color';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';
const COLOR_SWATCHES = [
  { label: 'Violet (Default)', hsl: DEFAULT_PRIMARY },
  { label: 'Indigo', hsl: '239 84% 67%' },
  { label: 'Blue', hsl: '221 83% 53%' },
  { label: 'Sky', hsl: '199 89% 48%' },
  { label: 'Teal', hsl: '172 66% 37%' },
  { label: 'Green', hsl: '142 76% 36%' },
  { label: 'Amber', hsl: '38 92% 50%' },
  { label: 'Orange', hsl: '25 95% 53%' },
  { label: 'Red', hsl: '0 84% 60%' },
  { label: 'Rose', hsl: '347 77% 50%' },
  { label: 'Pink', hsl: '328 86% 56%' },
  { label: 'Purple', hsl: '280 84% 52%' },
];

function WcagBadge({ hsl }: { hsl: string }) {
  const ratio = getContrastVsWhite(hsl);
  const display = ratio.toFixed(1);
  if (ratio >= 4.5) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
        ✓ AA · {display}:1
      </span>
    );
  }
  if (ratio >= 3) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">
        ⚠ AA Large · {display}:1
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
      ✗ Fail · {display}:1
    </span>
  );
}

function MiniDashboardPreview({ primary, mode }: { primary: string; mode: 'light' | 'dark' }) {
  const isDark = mode === 'dark';
  const col = {
    bg: isDark ? '#0a0a0a' : '#f0f0f2',
    sidebar: isDark ? '#111111' : '#ffffff',
    sidebarBorder: isDark ? '#1f1f1f' : '#e8e8ec',
    border: isDark ? '#2a2a2a' : '#e2e2e8',
    text: isDark ? '#f0f0f0' : '#111111',
    muted: isDark ? '#666666' : '#888888',
    card: isDark ? '#1a1a1a' : '#ffffff',
    input: isDark ? '#1e1e1e' : '#ffffff',
    primary: `hsl(${primary})`,
    primaryAlpha: isDark ? `hsl(${primary} / 0.15)` : `hsl(${primary} / 0.08)`,
    shadow: `0 2px 7px hsl(${primary} / 0.40)`,
    shadowSm: `0 1px 5px hsl(${primary} / 0.35)`,
  };
  const NAV = ['Dashboard', 'Members', 'Directory', 'Events', 'Projects'];
  const TABS = ['Business', 'Projects', 'Social'];
  const SUBS = ['General', 'Leads', 'Thank You', 'Testimonial', 'Meetup'];
  const PILLS = ['All', 'My Leads', 'Direct', 'Open', 'Interested'];

  return (
    <div
      style={{
        background: col.bg,
        border: `1px solid ${col.sidebarBorder}`,
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        height: 220,
        fontFamily: 'system-ui,sans-serif',
        userSelect: 'none',
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 108,
          background: col.sidebar,
          borderRight: `1px solid ${col.sidebarBorder}`,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: '8px 0',
          gap: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0 10px 8px' }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: 3,
              background: col.primary,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 9.5, fontWeight: 700, color: col.text }}>Milora</span>
        </div>
        <div
          style={{
            padding: '0 10px 3px',
            fontSize: 7,
            fontWeight: 600,
            color: col.muted,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          Navigation
        </div>
        {NAV.map((item, i) => (
          <div
            key={item}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4.5px 10px',
              borderLeft: i === 0 ? `3px solid ${col.primary}` : '3px solid transparent',
              background: i === 0 ? col.primaryAlpha : 'transparent',
            }}
          >
            <div
              style={{
                width: 9,
                height: 9,
                borderRadius: 2,
                background: i === 0 ? col.primary : col.muted,
                opacity: i === 0 ? 1 : 0.45,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 9,
                color: i === 0 ? col.primary : col.muted,
                fontWeight: i === 0 ? 600 : 400,
              }}
            >
              {item}
            </span>
          </div>
        ))}
      </div>
      {/* Main */}
      <div
        style={{
          flex: 1,
          padding: '9px 9px 7px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
        }}
      >
        {/* Top tabs */}
        <div style={{ display: 'flex', gap: 4 }}>
          {TABS.map((tab, i) => (
            <div
              key={tab}
              style={{
                padding: '3px 8px',
                borderRadius: 6,
                fontSize: 8.5,
                fontWeight: i === 0 ? 700 : 400,
                border: `1.5px solid ${i === 0 ? col.primary : col.border}`,
                color: i === 0 ? col.primary : col.muted,
                flexShrink: 0,
                boxShadow: i === 0 ? col.shadow : 'none',
              }}
            >
              {tab}
            </div>
          ))}
        </div>
        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 3, overflow: 'hidden' }}>
          {SUBS.map((tab, i) => (
            <div
              key={tab}
              style={{
                padding: '2.5px 6px',
                borderRadius: 5,
                fontSize: 7.5,
                fontWeight: i === 1 ? 700 : 400,
                background: i === 1 ? col.primary : 'transparent',
                color: i === 1 ? '#fff' : col.muted,
                border: `1px solid ${i === 1 ? col.primary : col.border}`,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                boxShadow: i === 1 ? col.shadow : 'none',
              }}
            >
              {tab}
            </div>
          ))}
        </div>
        {/* Search bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: col.input,
            border: `1px solid ${col.border}`,
            borderRadius: 6,
            padding: '3px 7px',
          }}
        >
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              border: `1px solid ${col.muted}`,
              opacity: 0.5,
            }}
          />
          <div
            style={{ width: 45, height: 5, background: col.muted, borderRadius: 2, opacity: 0.3 }}
          />
        </div>
        {/* Filter pills */}
        <div style={{ display: 'flex', gap: 3, overflow: 'hidden' }}>
          {PILLS.map((pill, i) => (
            <div
              key={pill}
              style={{
                padding: '2px 7px',
                borderRadius: 99,
                fontSize: 7.5,
                background: i === 0 ? col.primary : 'transparent',
                color: i === 0 ? '#fff' : col.muted,
                border: `1px solid ${i === 0 ? col.primary : col.border}`,
                flexShrink: 0,
                boxShadow: i === 0 ? col.shadowSm : 'none',
              }}
            >
              {pill}
            </div>
          ))}
        </div>
        {/* Card */}
        <div
          style={{
            background: col.card,
            border: `1px solid ${col.border}`,
            borderRadius: 7,
            padding: '6px 8px',
            flex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: col.primary,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ fontSize: 7, color: '#fff', fontWeight: 700 }}>AK</span>
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  width: 55,
                  height: 6,
                  background: col.text,
                  borderRadius: 2,
                  opacity: 0.85,
                  marginBottom: 2,
                }}
              />
              <div
                style={{
                  width: 38,
                  height: 4,
                  background: col.muted,
                  borderRadius: 2,
                  opacity: 0.45,
                }}
              />
            </div>
            <div
              style={{
                padding: '2px 5px',
                borderRadius: 4,
                fontSize: 7,
                border: `1px solid ${col.primary}`,
                color: col.primary,
                flexShrink: 0,
                fontWeight: 500,
              }}
            >
              Open Lead
            </div>
          </div>
          <div
            style={{
              width: '70%',
              height: 6.5,
              background: col.text,
              borderRadius: 2,
              opacity: 0.75,
              marginBottom: 3,
            }}
          />
          <div
            style={{
              width: '44%',
              height: 4.5,
              background: col.muted,
              borderRadius: 2,
              opacity: 0.35,
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface GeneralSettingsProps {
  communityId: string;
  mutateCommunity: KeyedMutator<{ community: Community }>;
  name: string;
  setName: (val: string) => void;
  slug: string;
  setSlug: (val: string) => void;
  logo: string;
  setLogo: (val: string) => void;
  banner: string;
  setBanner: (val: string) => void;
  originalSlug: string;
  setOriginalSlug: (val: string) => void;
  navigationConfig?: NavigationConfig | null;
  setNavigationConfig?: (config: NavigationConfig) => void;
  showNavigation?: boolean;
  inviteLink: string;
  setInviteLink: (val: string) => void;
  inviteCode: string;
  setInviteCode: (val: string) => void;
  inviteCodeEnabled: boolean;
  themeColors?: { primary?: string | null } | null;
}

export function GeneralSettings({
  communityId,
  mutateCommunity,
  name,
  setName,
  slug,
  setSlug,
  logo,
  setLogo,
  banner,
  setBanner,
  originalSlug,
  setOriginalSlug,
  navigationConfig,
  setNavigationConfig,
  showNavigation = true,
  inviteLink,
  setInviteLink,
  inviteCode,
  inviteCodeEnabled,
  themeColors,
}: GeneralSettingsProps) {
  const [uploading, setUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [slugLoading, setSlugLoading] = useState(false);

  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [draftPrimary, setDraftPrimary] = useState<string | null>(
    () => themeColors?.primary ?? null
  );
  const [savingColor, setSavingColor] = useState(false);
  const savedPrimary = themeColors?.primary ?? null;
  const [customColors, setCustomColors] = useState<string[]>([]);
  const [pickerColor, setPickerColor] = useState<string>(DEFAULT_PRIMARY);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [hexInput, setHexInput] = useState('');

  useEffect(() => {
    const value = draftPrimary ?? DEFAULT_PRIMARY;
    const communityRoot = document.getElementById('community-theme-root');
    if (communityRoot) {
      communityRoot.style.setProperty('--primary', value);
      communityRoot.style.setProperty('--ring', value);
    }
    document.documentElement.style.setProperty('--primary', value);
    document.documentElement.style.setProperty('--ring', value);
    notifyNativeSafeAreaTheme();
    return () => {
      const saved = savedPrimary ?? DEFAULT_PRIMARY;
      if (communityRoot) {
        communityRoot.style.setProperty('--primary', saved);
        communityRoot.style.setProperty('--ring', saved);
      }
      document.documentElement.style.setProperty('--primary', saved);
      document.documentElement.style.setProperty('--ring', saved);
      notifyNativeSafeAreaTheme();
    };
  }, [draftPrimary, savedPrimary]);

  // Auto-save Name on blur
  const saveName = async () => {
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, { name });
      await mutateCommunity();
      toast.success('Name updated');
    } catch (_error) {
      toast.error('Failed to update name');
    }
  };

  // Slug update logic (kept manual for safety/complexity, or debounced)
  const handleUpdateSlug = async () => {
    if (slug === originalSlug) return;
    setSlugLoading(true);
    try {
      const res = await axiosInstance.patch(`/api/communities/${communityId}`, {
        slug,
      });
      if (res.data?.community?.inviteLink) {
        setInviteLink(res.data.community.inviteLink);
      }
      setOriginalSlug(slug);
      await mutateCommunity();
      toast.success('Slug and invite link updated');
    } catch (error) {
      let msg = 'Failed to update slug';
      if (error instanceof AxiosError && error.response?.data?.error) {
        msg = error.response.data.error;
      }
      toast.error(msg);
    } finally {
      setSlugLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'community-logos');

    try {
      const { url: newLogoUrl } = await postMultipartImageUpload(formData);
      setLogo(newLogoUrl);

      await axiosInstance.patch(`/api/communities/${communityId}`, { logo: newLogoUrl });
      await mutateCommunity();
      toast.success('Logo updated');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBannerUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', `community-banners/${communityId}`);

    try {
      const { url: newBannerUrl } = await postMultipartImageUpload(formData);
      setBanner(newBannerUrl);

      await axiosInstance.patch(`/api/communities/${communityId}`, { banner: newBannerUrl });
      await mutateCommunity();
      toast.success('Dashboard banner updated');
    } catch (error) {
      console.error('Error uploading banner:', error);
      toast.error('Failed to upload banner');
    } finally {
      setBannerUploading(false);
    }
  };

  const handleRemoveBanner = async () => {
    setBannerUploading(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, { banner: null });
      setBanner('');
      await mutateCommunity();
      toast.success('Dashboard banner removed');
    } catch (error) {
      console.error('Error removing banner:', error);
      toast.error('Failed to remove banner');
    } finally {
      setBannerUploading(false);
    }
  };

  const resetToDefault = async () => {
    setSavingColor(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        themeColors: { primary: DEFAULT_PRIMARY },
      });
      await mutateCommunity();
      setDraftPrimary(DEFAULT_PRIMARY);
      toast.success('Brand colour reset to default');
    } catch (_error) {
      toast.error('Failed to reset brand colour');
    } finally {
      setSavingColor(false);
    }
  };

  const saveThemeColor = async () => {
    setSavingColor(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        themeColors: draftPrimary ? { primary: draftPrimary } : {},
      });
      await mutateCommunity();
      toast.success('Brand colour updated');
    } catch (_error) {
      toast.error('Failed to update brand colour');
    } finally {
      setSavingColor(false);
    }
  };

  const copyInviteLink = () => {
    const appUrl = window.location.origin;
    const fullLink = `${appUrl}/invite/${inviteLink}`;
    navigator.clipboard
      .writeText(fullLink)
      .then(() => {
        setCopied(true);
        toast.success('Invite link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy link — please copy it manually');
      });
  };

  const copyInviteCode = () => {
    if (!inviteCode) return;
    navigator.clipboard
      .writeText(inviteCode)
      .then(() => {
        setCopiedCode(true);
        toast.success('Invite code copied to clipboard');
        setTimeout(() => setCopiedCode(false), 2000);
      })
      .catch(() => toast.error('Failed to copy code'));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Community Profile</CardTitle>
          <CardDescription>Update your community's basic information.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={logo} className="object-cover" />
              <AvatarFallback>{name?.substring(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-2">
              <Label htmlFor="logo-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent transition-colors">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span>{uploading ? 'Uploading...' : 'Change Logo'}</span>
                </div>
                <Input
                  id="logo-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
              </Label>
              <p className="text-xs text-muted-foreground">
                Recommended: Square image, at least 400x400px
              </p>
            </div>
          </div>

          <div className="space-y-3 border-t border-border/40 pt-6">
            <div>
              <Label>Dashboard Banner</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Shown at the top of the community home page. Recommended: wide image, at least
                1200×400px.
              </p>
            </div>
            {banner ? (
              <div className="overflow-hidden rounded-xl border border-border">
                <img
                  src={banner}
                  alt="Community dashboard banner preview"
                  className="block h-auto max-h-40 w-full object-cover"
                />
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="banner-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-accent transition-colors">
                  {bannerUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span>
                    {bannerUploading ? 'Saving…' : banner ? 'Replace Banner' : 'Upload Banner'}
                  </span>
                </div>
                <Input
                  id="banner-upload"
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleBannerUpload}
                  disabled={bannerUploading}
                />
              </Label>
              {banner ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemoveBanner}
                  disabled={bannerUploading}
                >
                  Remove
                </Button>
              ) : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Community Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              placeholder="e.g. tech-enthusiasts"
            />
            <p className="text-xs text-muted-foreground">
              This is the visible name of your community.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">URL Slug</Label>
            <div className="flex gap-2">
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="my-community"
              />
              <Button
                onClick={handleUpdateSlug}
                disabled={slug === originalSlug || slugLoading}
                variant="secondary"
              >
                {slugLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Changing this will invalidate existing invite links.
            </p>
            {!inviteCodeEnabled && (
              <p className="text-xs font-bold text-foreground">
                Invite code and link disabled — to enable, update Privacy Settings.
              </p>
            )}
          </div>

          {inviteCodeEnabled && (
            <div className="space-y-2">
              <Label>Invitation Code</Label>
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center justify-center rounded-xl border border-border bg-muted/40 px-4 py-3">
                  <span className="font-mono text-2xl font-bold tracking-[0.25em] text-foreground select-all">
                    {inviteCode || '——————'}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyInviteCode}
                  disabled={!inviteCode}
                  title="Copy invite code"
                  className="h-12 w-12 shrink-0"
                >
                  {copiedCode ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this 6-character code with others to invite them to your community. The code
                remains the same even if the invite link is regenerated.
              </p>
            </div>
          )}

          {inviteCodeEnabled && (
            <div className="space-y-2">
              <Label>Invite Link</Label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink ? `${window.location.origin}/invite/${inviteLink}` : ''}
                  readOnly
                  className="font-mono text-sm"
                  placeholder="No invite link generated"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyInviteLink}
                  disabled={!inviteLink}
                  title="Copy invite link"
                >
                  {copied ? (
                    <span className="text-green-500 text-sm font-bold">✓</span>
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Share this link with others to invite them to your community.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Brand Colour</CardTitle>
          <CardDescription>
            Set a custom accent colour for this community. Affects buttons, active states, links,
            and focus rings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Swatch box */}
          <div className="rounded-xl border p-4 space-y-3 bg-[hsl(240_8%_95%)] border-[hsl(240_8%_88%)] dark:bg-[hsl(240_10%_6%)] dark:border-[hsl(240_10%_13%)]">
            <Label>Select colour</Label>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setDraftPrimary(null);
                  setColorPickerOpen(false);
                }}
                title="Milora default (violet)"
                className={`relative h-9 w-9 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                  draftPrimary === null
                    ? 'scale-110 ring-2 ring-offset-2 ring-foreground/60'
                    : 'hover:scale-105 opacity-80 hover:opacity-100'
                }`}
                style={{ backgroundColor: `hsl(${DEFAULT_PRIMARY})` }}
              >
                {draftPrimary === null && (
                  <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
                )}
              </button>
              {COLOR_SWATCHES.map((s) => (
                <button
                  key={s.hsl}
                  type="button"
                  onClick={() => {
                    setDraftPrimary(s.hsl);
                    setColorPickerOpen(false);
                  }}
                  title={s.label}
                  className={`relative h-9 w-9 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    draftPrimary === s.hsl
                      ? 'scale-110 ring-2 ring-offset-2 ring-foreground/60'
                      : 'hover:scale-105 opacity-80 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: `hsl(${s.hsl})` }}
                >
                  {draftPrimary === s.hsl && (
                    <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
                  )}
                </button>
              ))}
              {/* Added custom swatches */}
              {customColors.map((hsl, idx) => (
                <div key={`custom-${idx}`} className="relative group">
                  <button
                    type="button"
                    onClick={() => {
                      setDraftPrimary(hsl);
                      setColorPickerOpen(false);
                    }}
                    title={`Custom colour ${idx + 1}`}
                    className={`relative h-9 w-9 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                      draftPrimary === hsl
                        ? 'scale-110 ring-2 ring-offset-2 ring-foreground/60'
                        : 'hover:scale-105 opacity-80 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: `hsl(${hsl})` }}
                  >
                    {draftPrimary === hsl && (
                      <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow" />
                    )}
                  </button>
                  <button
                    type="button"
                    title="Remove colour"
                    onClick={() => {
                      setCustomColors((prev) => prev.filter((_, i) => i !== idx));
                      if (draftPrimary === hsl) setDraftPrimary(savedPrimary);
                    }}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-background border border-border text-muted-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-white hover:border-destructive"
                  >
                    <span className="text-[9px] font-bold leading-none">✕</span>
                  </button>
                </div>
              ))}
              {/* Colour wheel picker toggle — always at the end */}
              <button
                type="button"
                title="Pick custom colour"
                onClick={() => {
                  if (!colorPickerOpen) {
                    const base = draftPrimary ?? DEFAULT_PRIMARY;
                    setPickerColor(base);
                    setHexInput(hslToHex(base).slice(1));
                  }
                  setColorPickerOpen((v) => !v);
                }}
                className={`h-9 w-9 rounded-full flex items-center justify-center transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 hover:scale-105 ${colorPickerOpen ? 'scale-110 ring-2 ring-offset-2 ring-foreground/60' : ''}`}
                style={{ border: '2px dashed hsl(240 5% 55%)' }}
              >
                <Palette className="h-4 w-4 text-muted-foreground" />
              </button>
            </div>

            {/* Colour picker panel — inline in document flow, never overflows card */}
            {colorPickerOpen && (
              <div className="rounded-xl border border-border bg-popover shadow-md p-3 space-y-3 w-full sm:w-[240px] sm:ml-auto">
                <HslColorPicker
                  color={hslStringToObj(pickerColor)}
                  onChange={(obj) => {
                    const hsl = objToHslString(obj);
                    setPickerColor(hsl);
                    setDraftPrimary(hsl);
                    setHexInput(hslToHex(hsl).slice(1));
                  }}
                  style={{ width: '100%', height: 190 }}
                />
                <div className="flex items-center gap-2">
                  <div
                    className="h-7 w-7 rounded-full border border-border flex-shrink-0"
                    style={{ backgroundColor: `hsl(${pickerColor})` }}
                  />
                  <div className="flex items-center gap-1 flex-1 border border-border rounded-md px-2 py-1.5 bg-muted/30">
                    <span className="text-xs text-muted-foreground font-mono">#</span>
                    <input
                      className="flex-1 text-xs font-mono bg-transparent outline-none uppercase"
                      value={hexInput}
                      maxLength={6}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9a-fA-F]/g, '');
                        setHexInput(val);
                        if (val.length === 6) {
                          const hsl = hexToHsl(`#${val}`);
                          setPickerColor(hsl);
                          setDraftPrimary(hsl);
                        }
                      }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!customColors.includes(pickerColor)) {
                      setCustomColors((prev) => [...prev, pickerColor]);
                    }
                    setDraftPrimary(pickerColor);
                    setColorPickerOpen(false);
                  }}
                  className="w-full py-1.5 rounded-md text-sm font-semibold text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: `hsl(${pickerColor})` }}
                >
                  Add colour
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <WcagBadge hsl={draftPrimary ?? DEFAULT_PRIMARY} />
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] text-xs space-y-2" side="top">
                  <p className="font-semibold text-sm">WCAG Contrast Ratio</p>
                  <p className="text-muted-foreground">
                    Measures if white text on your brand colour is readable. Contrast is calculated
                    against white (used for button labels).
                  </p>
                  <div className="space-y-1.5 pt-1">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
                        ✓ AA
                      </span>
                      <span className="text-muted-foreground">
                        ≥ 4.5:1 — safe for all text sizes
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800">
                        ⚠ AA Large
                      </span>
                      <span className="text-muted-foreground">
                        ≥ 3.0:1 — ok for large/bold text only
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800">
                        ✗ Fail
                      </span>
                      <span className="text-muted-foreground">
                        {'< 3.0:1 — hard to read, avoid'}
                      </span>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              {draftPrimary === null && (
                <p className="text-xs" style={{ color: 'hsl(240 5% 55%)' }}>
                  Using Milora default (violet)
                </p>
              )}
            </div>
          </div>

          {/* Preview boxes — fixed light / dark backgrounds regardless of current theme */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className="rounded-xl border p-3 flex flex-col gap-2.5"
              style={{ background: 'hsl(240 8% 95%)', borderColor: 'hsl(240 8% 85%)' }}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base leading-none">☀️</span>
                <span className="text-[11px] font-semibold" style={{ color: 'hsl(240 10% 15%)' }}>
                  Light mode
                </span>
              </div>
              <MiniDashboardPreview primary={draftPrimary ?? DEFAULT_PRIMARY} mode="light" />
            </div>
            <div
              className="rounded-xl border p-3 flex flex-col gap-2.5"
              style={{ background: 'hsl(240 10% 6%)', borderColor: 'hsl(240 10% 13%)' }}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base leading-none">🌙</span>
                <span className="text-[11px] font-semibold" style={{ color: 'hsl(240 10% 85%)' }}>
                  Dark mode
                </span>
              </div>
              <MiniDashboardPreview primary={draftPrimary ?? DEFAULT_PRIMARY} mode="dark" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={saveThemeColor}
              disabled={savingColor || draftPrimary === savedPrimary}
              size="sm"
            >
              {savingColor ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Colour
            </Button>
            {draftPrimary !== savedPrimary && (
              <button
                type="button"
                onClick={() => setDraftPrimary(savedPrimary)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Discard
              </button>
            )}
            {((savedPrimary !== null && savedPrimary !== DEFAULT_PRIMARY) ||
              (draftPrimary !== null && draftPrimary !== DEFAULT_PRIMARY)) && (
              <button
                type="button"
                onClick={resetToDefault}
                disabled={savingColor}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                Reset to default
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {showNavigation && navigationConfig !== undefined && setNavigationConfig ? (
        <NavigationSettings
          communityId={communityId}
          mutateCommunity={mutateCommunity}
          navigationConfig={navigationConfig}
          setNavigationConfig={setNavigationConfig}
        />
      ) : null}
    </div>
  );
}
